/**
 * NotificationCenter — the console's binding of the shared notification bell.
 *
 * The bell itself now lives in `@solum/ui` (`shell/NotificationBell.tsx`, #1115)
 * because the customer portal needed the same one and an app cannot import from
 * another app. What is left here is what a bell must NOT know: which hooks read
 * the spine, and what a destination is.
 *
 * The console's destination is a ROUTE. `core.notification.action_url` is
 * written by producers as a console path (`/cpq/…`, `/e/<entity>/…`), so
 * following it is right here and is exactly what the portal must not do — see
 * the component's header.
 *
 * Slice 1 remains the in-app channel (a gentle poll); the relay push
 * (email/Teams) is the next slice.
 */
import { useNavigate } from '@tanstack/react-router'
import { NotificationBell } from '@solum/ui'
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead } from '../data/queries'

export function NotificationCenter() {
  const navigate = useNavigate()
  const go = (to: string) => (navigate as unknown as (o: { to: string }) => void)({ to })
  const { data: items = [] } = useNotifications()
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()

  return (
    <NotificationBell
      items={items}
      onMarkRead={(id) => markRead.mutate(id)}
      onMarkAll={() => markAll.mutate()}
      // `true` closes the panel behind the navigation; an item with no
      // action_url leaves it open, which is the behaviour this had before the
      // extraction and the reason `onOpen` returns anything at all.
      onOpen={(n) => { if (!n.action_url) return false; go(n.action_url); return true }}
    />
  )
}
